/**
 * 标注插件 —— 实现 Plugin + AnnotationService，提供标注 CRUD 管理与交互插入。
 * 不实现 ToolPlugin，不参与工具互斥。
 */
import type * as THREE from 'three'
import { WEBGL2_ONLY_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import {
  getActiveViewCamera,
  getActiveViewId,
  getActiveViewRect,
  getOptionalViewRegistry,
  isActiveViewFrame,
} from '@/plugins/tools/shared/viewScope'
import type { EngineContext } from '@/runtime/EngineContext'
import type {
  AnnotationData,
  AnnotationHandle,
  InputConsumer,
  NormalizedKeyEvent,
  NormalizedPointerEvent,
} from '@/shared/types/core'
import type { Plugin } from '@/shared/types/plugin'
import type {
  AnnotationService,
  InputRouter,
  PickingService,
  ViewRegistry,
} from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import { generateId } from '@/shared/utils/index'
import { toViewportLocalPoint } from '@/shared/utils/viewRect'

/** 内部标注条目 */
interface AnnotationEntry {
  data: AnnotationData
  handle: AnnotationHandle
}

/** 标注插件，同时实现 AnnotationService */
export class AnnotationPlugin implements Plugin, AnnotationService {
  /** 插件名称 */
  readonly name = 'annotation'
  readonly graphicsRequirements = WEBGL2_ONLY_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级，数值越小越先启动 */
  readonly priority = 50
  /** 依赖的服务名列表 */
  readonly dependencies = ['PickingService', 'SelectionService', 'InputRouter'] as const
  /** 对外提供的服务名列表 */
  readonly provides = ['AnnotationService'] as const

  private context!: EngineContext

  /** 标注数据存储：id → 条目 */
  private annotations = new Map<string, AnnotationEntry>()

  /** 输入路由服务 */
  private inputRouter!: InputRouter

  /** 拾取服务 */
  private pickingService!: PickingService

  /** 可选多视口注册表 */
  private viewRegistry: ViewRegistry | null = null

  /** 缓存当前帧相机引用，供输入回调使用 */
  private currentCamera: THREE.Camera | null = null

  /** 是否正在交互插入中 */
  private _inserting = false

  /** 插入时携带的预设数据 */
  private _insertionData: Partial<AnnotationData> | null = null

  /** 插入流程的输入消费者 */
  private _insertionConsumer: InputConsumer | null = null

  private readonly handleViewActivated = (event: {
    viewId: string
    previousViewId: string | null
  }) => {
    this.currentCamera = this.resolveActiveViewCamera()
    if (event.previousViewId && event.previousViewId !== event.viewId && this._inserting) {
      this.cancelInsertion()
    }
  }

  // ── Plugin 生命周期 ──

  /**
   * 初始化插件，注册 AnnotationService。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
    context.registerService('AnnotationService', this)
  }

  /** 启动插件，解析依赖并订阅视口激活事件 */
  onStart(): void {
    this.inputRouter = this.context.getService('InputRouter')
    this.pickingService = this.context.getService('PickingService')
    this.viewRegistry = getOptionalViewRegistry(this.context)
    this.currentCamera = this.resolveActiveViewCamera()
    if (this.viewRegistry) {
      this.context.events.on('view.activated', this.handleViewActivated)
    }
  }

  /**
   * 渲染视图前更新标注可见性。
   *
   * @param frame - 视图帧上下文
   */
  onRenderView(frame: ViewFrameContext): void {
    if (!isActiveViewFrame(this.viewRegistry, frame)) return

    this.currentCamera = this.resolveActiveViewCamera() ?? frame.camera
    if (this.annotations.size === 0) return
    const cameraPos = this.currentCamera.position
    this.annotations.forEach((entry) => {
      this.updateAnnotationVisibility(entry, cameraPos)
    })
  }

  /** 销毁插件，清理订阅与标注数据 */
  onDestroy(): void {
    if (this.viewRegistry) {
      this.context.events.off('view.activated', this.handleViewActivated)
    }
    if (this._inserting) {
      this._cleanupInsertion()
    }
    this.annotations.clear()
    this.viewRegistry = null
  }

  // ── AnnotationService 实现 ──

  /**
   * 创建标注。
   *
   * @param data - 标注数据，若未指定 id 则自动生成
   * @returns 新标注的操作句柄
   */
  create(data: AnnotationData): AnnotationHandle {
    const id = data.id || generateId()
    const fullData: AnnotationData = { ...data, id }
    const handle: AnnotationHandle = {
      id,
      anchor: fullData.position.clone(),
      visible: fullData.visible ?? true,
    }
    this.annotations.set(id, { data: fullData, handle })
    this.context.events.emit('annotation.created', { id })
    return handle
  }

  /**
   * 更新标注。
   *
   * @param id - 标注 ID
   * @param patch - 增量字段，只覆盖提供的键
   */
  update(id: string, patch: Partial<AnnotationData>): void {
    const entry = this.annotations.get(id)
    if (!entry) return
    Object.assign(entry.data, patch)
    if (patch.position) {
      entry.handle.anchor.copy(patch.position)
    }
    if (patch.visible !== undefined) {
      entry.handle.visible = patch.visible
    }
    this.context.events.emit('annotation.updated', { id })
  }

  /**
   * 移除标注。
   *
   * @param id - 标注 ID；不存在时忽略
   */
  remove(id: string): void {
    const entry = this.annotations.get(id)
    if (!entry) return
    this.annotations.delete(id)
    this.context.events.emit('annotation.removed', { id })
  }

  /**
   * 按 ID 获取标注。
   *
   * @param id - 标注 ID
   * @returns 标注句柄；不存在时返回 undefined
   */
  get(id: string): AnnotationHandle | undefined {
    return this.annotations.get(id)?.handle
  }

  /**
   * 获取所有标注。
   *
   * @returns 标注句柄数组；无标注返回空数组
   */
  getAll(): AnnotationHandle[] {
    const handles: AnnotationHandle[] = []
    this.annotations.forEach((e) => handles.push(e.handle))
    return handles
  }

  // ── 交互插入流程 ──

  /** 是否正在交互插入中 */
  get isInserting(): boolean {
    return this._inserting
  }

  /**
   * 开启交互插入流程，点击场景放置标注。
   *
   * @param data - 预设标注字段，放置时与位置合并；省略则全部为默认值
   */
  startInsertion(data?: Partial<AnnotationData>): void {
    // 幂等：已在插入中则忽略
    if (this._inserting) return

    this._inserting = true
    this._insertionData = data ?? {}

    const consumer: InputConsumer = {
      name: 'annotation-insertion',
      priority: 100,

      onPointerMove: (event: NormalizedPointerEvent): boolean => {
        const results = this.pickAtActiveView(event.screen)
        if (results.length > 0) {
          this.context.events.emit('annotation.insertionPreview', {
            position: results[0].point.clone(),
          })
        }
        return false
      },

      onPointerDown: (event: NormalizedPointerEvent): boolean => {
        // 右键取消
        if (event.button === 2) {
          this.cancelInsertion()
          return true
        }
        // 左键确认放置
        if (event.button === 0) {
          const results = this.pickAtActiveView(event.screen)
          if (results.length > 0) {
            const position = results[0].point.clone()
            this.create({
              position,
              title: '',
              content: '',
              ...this._insertionData,
            } as AnnotationData)
            this._finishInsertion()
            return true
          }
        }
        return false
      },

      onKeyDown: (event: NormalizedKeyEvent): boolean => {
        if (event.key === 'Escape') {
          this.cancelInsertion()
          return true
        }
        return false
      },
    }

    this._insertionConsumer = consumer
    this.inputRouter.addConsumer(consumer)
    this.context.events.emit('annotation.insertionStarted', {})
  }

  /** 取消交互插入 */
  cancelInsertion(): void {
    if (!this._inserting) return
    this._cleanupInsertion()
    this.context.events.emit('annotation.insertionCancelled', {})
  }

  // ── 私有方法 ──

  /** 完成交互插入 */
  private _finishInsertion(): void {
    this._cleanupInsertion()
    this.context.events.emit('annotation.insertionCompleted', {})
  }

  /** 清理插入状态，从 InputRouter 移除 consumer */
  private _cleanupInsertion(): void {
    if (this._insertionConsumer) {
      this.inputRouter.removeConsumer(this._insertionConsumer)
    }
    this._inserting = false
    this._insertionData = null
    this._insertionConsumer = null
  }

  /** 根据相机距离更新标注可见性 */
  private updateAnnotationVisibility(entry: AnnotationEntry, cameraPos: THREE.Vector3): void {
    // 超过阈值距离时隐藏标注（基于数据中的 visible 字段和距离判断）
    if (entry.data.visible === false) {
      entry.handle.visible = false
      return
    }
    const distance = cameraPos.distanceTo(entry.handle.anchor)
    // 标注在 1000 单位以外自动隐藏
    entry.handle.visible = distance < 1000
  }

  private resolveActiveViewCamera(): THREE.Camera | null {
    return getActiveViewCamera(this.viewRegistry)
  }

  private pickAtActiveView(screen: THREE.Vector2) {
    if (!this.currentCamera) {
      return []
    }

    const activeViewId = getActiveViewId(this.viewRegistry)
    const activeViewRect = getActiveViewRect(this.viewRegistry)
    const containerRect = this.context.container?.getBoundingClientRect?.()
    const scopedScreen =
      activeViewId || activeViewRect
        ? containerRect
          ? toViewportLocalPoint(screen, containerRect)
          : screen.clone()
        : screen
    return this.pickingService.pickAtScreen(scopedScreen, this.currentCamera, {
      viewId: activeViewId ?? undefined,
      viewRect: activeViewRect ?? undefined,
    })
  }
}

export default AnnotationPlugin
